<script>
import axios from 'axios';
import formatDate from '@/helpers/formatDate';
import { mapState } from 'vuex';
import ReportStep from '@/reports/ReportStep';
import notify from '@/store/Common/notify';

export default {
    components: { ReportStep },
    data() {
        return {
            tokenGenerated: false,
            tokenGenerating: false,
            urlGenerating: false,
            token: '',
            steps: [],
            userData: [],
            tableLoading: true,
            headers: [
                {
                    text: 'Step',
                    value: 'name'
                },
                {
                    text: 'Section',
                    value: 'section'
                }
            ]
        };
    },

    computed: {
        ...mapState('auth', ['user']),

        userId: function() {
            return this.$route.params.id;
        }
    },

    mounted() {
        this.getSite();
    },

    methods: {
        getSite() {
            this.tableLoading = true;
            this.steps = [];
            axios
                .get('/api/report/site/single/' + this.userId)
                .then((response) => {
                    this.steps = response.data.data.site;
                    this.userData = response.data.data.user;
                    this.tableLoading = false;
                })
                .catch((error) => {
                    this.error = error;
                    this.tableLoading = false;
                });
        },

        getUrl() {
            this.urlGenerating = true;
            axios
                .get('/api/report/site/api-url')
                .then((response) => {
                    console.log(response.data);
                    notify('URL Copied to Clipboard', 'success');
                    // This only works in https environments.
                    // Local http environments will default to the catch block.
                    navigator.clipboard.writeText(response.data);
                })
                .catch(() => {
                    notify('Unable to copy API URL. Please contact support.', 'error');
                })
                .finally(() => {
                    this.urlGenerating = false;
                });
        },

        displayDate(date) {
            return formatDate(date);
        },

        exportProject(id) {
            console.log(id);
        },

        generateToken() {
            this.tokenGenerating = true;
            axios.post('/api/user/create-own-token')
                .then(response => {
                    this.token = response.data;
                    notify('API Key Copied to Clipboard', 'success');
                    navigator.clipboard.writeText(response.data);
                })
                .catch(error => {
                    console.error(error);
                    notify('Unable to generate API Key. Please contact support.', 'error');
                })
                .finally(() => {
                    this.tokenGenerating = false;
                });
        }
    }
};
</script>

<template>
    <v-container fluid>
        <v-row
            align="center"
            justify="center"
            class="Report"
        >
            <v-col cols="11">
                <div class="Report__heading">
                    <h2 v-if="user.role === 'admin'">
                        Site: {{ userData.name }}
                    </h2>
                    <v-spacer />

                    <!-- Refresh Report -->
                    <v-tooltip
                        color="black"
                        left
                        open-delay="1000"
                        v-if="!tableLoading"
                    >
                        <template #activator="{on, attrs}">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                small
                                @click="getSite"
                            >
                                <v-icon small>
                                    fas fa-sync
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Refresh Report</span>
                    </v-tooltip>

                    <v-btn
                        icon
                        small
                        v-else-if="tableLoading"
                    >
                        <v-icon small>
                            fa-spin far fa-spinner
                        </v-icon>
                    </v-btn>

                    <!-- Url Copy Spinner -->
                    <v-btn
                        icon
                        small
                        v-if="urlGenerating"
                    >
                        <v-icon small>
                            fa-spin far fa-spinner
                        </v-icon>
                    </v-btn>

                    <!-- Copy URL -->
                    <v-tooltip
                        color="black"
                        left
                        open-delay="1000"
                        v-else-if="!urlGenerating"
                    >
                        <template #activator="{on, attrs}">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                small
                                @click="getUrl"
                            >
                                <v-icon small>
                                    fas fa-at
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Copy API URL</span>
                    </v-tooltip>

                    <!-- Generate API Key -->
                    <v-tooltip
                        color="black"
                        left
                        open-delay="1000"
                        v-if="!tokenGenerating"
                    >
                        <template #activator="{on, attrs}">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                icon
                                small
                                @click="generateToken"
                            >
                                <v-icon small>
                                    fas fas fa-key
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Copy API Key</span>
                    </v-tooltip>

                    <!-- Key Generation Spinner -->
                    <v-btn
                        icon
                        small
                        v-else-if="tokenGenerating"
                    >
                        <v-icon small>
                            fa-spin far fa-spinner
                        </v-icon>
                    </v-btn>
                </div>

                <div class="Report__report">
                    <v-progress-linear
                        indeterminate
                        v-if="tableLoading"
                    />
                    <div
                        v-if="steps"
                        class="Report__steps"
                    >
                        <ReportStep
                            v-for="(step, index) in steps"
                            :key="index"
                            :step="step"
                        />
                    </div>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<style lang="scss">
.Report {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
}

.Report__heading {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 24px;
}

.Report__steps {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
}
</style>
